import { useState } from 'react';
import { TurnClock } from './Liveness';
import {
  Ban, CheckCircle2, ChevronDown, ChevronRight, CircleDashed, Loader2, PauseCircle, XCircle,
} from 'lucide-react';

/**
 * The agent rail: every hypothesis, what state it is in, and how sure it is.
 *
 * Refuted hypotheses are shown, not hidden. An engagement that honestly ruled things
 * out is worth more than one that reports maybes, and a rail that only lists
 * wins makes the work look thinner than it was.
 */

export const HYPOTHESIS_META = {
  // 'Under test', not 'Working'. This map is keyed on a HYPOTHESIS status and
  // is rendered in the header beside "2 of 4 agents on the target", so
  // "2 Working" read as two agents while the banner below said "1 agent
  // working" and the roster showed three in flight. Three counters, three
  // different nouns, each correct alone and unreadable together. An angle is
  // under test; an agent works.
  investigating: { label: 'Under test', icon: Loader2, tone: 'text-wasabi-700', dot: 'bg-wasabi-500', spin: true },
  queued: { label: 'Queued', icon: CircleDashed, tone: 'text-slate-500', dot: 'bg-slate-300' },
  confirmed: { label: 'Confirmed', icon: CheckCircle2, tone: 'text-red-700', dot: 'bg-red-500' },
  refuted: { label: 'Refuted', icon: XCircle, tone: 'text-slate-500', dot: 'bg-slate-400' },
  blocked: { label: 'Blocked', icon: PauseCircle, tone: 'text-amber-700', dot: 'bg-amber-500' },
  abandoned: { label: 'Abandoned', icon: Ban, tone: 'text-slate-400', dot: 'bg-slate-300' },
};

//: Working first, then anything that needs a look, then settled outcomes.
const ORDER = ['investigating', 'blocked', 'queued', 'confirmed', 'refuted', 'abandoned'];

/**
 * The three states that mean "we stopped".
 *
 * Kept behind an expander rather than listed. By the end of an engagement the
 * rail is a dozen rows, eleven of them finished, and the one the operator wants
 * is whatever just changed. `blocked` is deliberately NOT here: it looks
 * settled and is not - it is the state an operator can act on by reopening the
 * angle, so burying it would hide the one row with something to do.
 */
const SETTLED = ['confirmed', 'refuted', 'abandoned'];

/** Newest first, within a state. */
function byNewest(a, b) {
  const at = Date.parse(a.verdict_at || a.created_at || 0) || 0;
  const bt = Date.parse(b.verdict_at || b.created_at || 0) || 0;
  if (bt !== at) return bt - at;
  return (b.id || 0) - (a.id || 0);
}

//: Why this angle is where it is, for the chip's tooltip.
//
//: "Blocked" on its own is the least actionable word on the screen: it looks
//: like a malfunction, when it usually means the agent honestly established
//: that the claim cannot be tested with the instruments available. On pentest
//: 44 the reason was three identical probe refusals followed by a considered
//: conclude(blocked), and none of it was visible.
function stateExplanation(hypothesis, meta) {
  const why = hypothesis.blocked_reason || hypothesis.verdict_reason;
  if (hypothesis.status === 'blocked') {
    return why
      ? `Blocked: ${why}`
      : 'The agent could not get there with the actions available to it. '
        + 'This is not a refutation: nothing was established about the target. '
        + 'Open the angle to read its turns, or retask it.';
  }
  if (hypothesis.status === 'refuted') {
    return why ? `Ruled out: ${why}` : 'Tested and ruled out.';
  }
  if (hypothesis.status === 'queued') {
    return 'Approved and waiting for a free agent slot.';
  }
  return why || meta.label;
}

/** One row. Extracted so the live list and the collapsed history share it. */
function AngleRow({ hypothesis, worker, active, onSelect, now }) {
  const meta = HYPOTHESIS_META[hypothesis.status] || HYPOTHESIS_META.queued;
  const Icon = meta.icon;
  return (
    <button
      type="button"
      data-testid={`angle-row-${hypothesis.id}`}
      onClick={() => onSelect(hypothesis.id)}
      className={`w-full rounded-md border-l-2 px-2 py-1.5 text-left transition
        motion-safe:animate-row-arrive
        ${active
          ? 'border-l-wasabi-500 bg-wasabi-50'
          : 'border-l-transparent hover:bg-slate-50'}`}
    >
      <div className="flex items-start gap-1.5">
        <Icon
          className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${meta.tone} ${meta.spin ? 'animate-spin' : ''}`}
        />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-xs font-medium text-slate-900">
            {hypothesis.title}
          </span>
          <span className="flex items-center gap-1.5 text-[11px] text-slate-500">
            {/* A Blocked angle used to say only "Blocked", so the
                operator clicked it repeatedly looking for a reason that
                was never rendered. The reason always existed: the agent
                concluded blocked and said why. */}
            <span title={stateExplanation(hypothesis, meta)}>{meta.label}</span>
            {hypothesis.source === 'agent' && (
              <span title="Emitted by another agent" className="text-wasabi-600">
                &middot; emitted
              </span>
            )}
          </span>
        </span>
        {/* TURNS PLUS A SECOND HAND, and the second hand is what makes it
            readable. This slot printed `hypothesis.confidence` once, which
            never moves until the verdict; then it printed the turn count,
            which is the right number and steps once every couple of MINUTES.
            The operator read a static "0/40" for four minutes on pentest 51
            and reported the progress as frozen. It was correct and slow.

            TurnClock adds elapsed-time-on-this-turn and a pulse, so a glance
            says "live" and a read says how far along. See Liveness.js.

            Only while under test: a settled angle's turn count is history and
            belongs in the detail pane, not in a rail the operator scans. */}
        {hypothesis.status === 'investigating' && worker && (
          <TurnClock run={worker} now={now} className="shrink-0 text-[11px] text-slate-500" />
        )}
      </div>
    </button>
  );
}

export default function AgentRail({ hypotheses, roster, selectedId, onSelect, now }) {
  const [showSettled, setShowSettled] = useState(false);

  if (!hypotheses?.length) {
    return (
      <p className="px-1 text-xs text-slate-500">
        No angles yet. Propose some from findings, or run the next step and the
        Lead will draft a plan.
      </p>
    );
  }

  const workerFor = (id) =>
    (roster || []).find((m) => m.role !== 'lead' && m.hypothesis_id === id);

  const sort = (rows) => [...rows].sort((a, b) => {
    const byState = ORDER.indexOf(a.status) - ORDER.indexOf(b.status);
    if (byState !== 0) return byState;
    // NEWEST FIRST within a state, where it used to be highest priority
    // first. Priority is the Lead's ranking of what matters and it is a fine
    // tiebreak in a plan; in a live rail the operator is looking for the row
    // that just changed, and on an engagement where every angle was framed in
    // one pass the priorities are near-identical anyway.
    return byNewest(a, b);
  });

  const live = sort(hypotheses.filter((h) => !SETTLED.includes(h.status)));
  const settled = sort(hypotheses.filter((h) => SETTLED.includes(h.status)));

  const row = (h) => (
    <AngleRow
      key={h.id}
      hypothesis={h}
      worker={workerFor(h.id)}
      active={h.id === selectedId}
      onSelect={onSelect}
      now={now}
    />
  );

  return (
    <div className="space-y-1">
      {live.map(row)}

      {settled.length > 0 && (
        <>
          <button
            type="button"
            data-testid="angles-settled-toggle"
            aria-expanded={showSettled}
            onClick={() => setShowSettled((v) => !v)}
            className="flex w-full items-center gap-1 rounded-md px-2 py-1.5 text-left text-[11px] font-semibold text-slate-500 transition hover:bg-slate-50 hover:text-slate-800"
          >
            {showSettled
              ? <ChevronDown className="h-3 w-3 shrink-0" />
              : <ChevronRight className="h-3 w-3 shrink-0" />}
            {/* The COUNT is on the closed state deliberately. Collapsing work
                the engagement actually did, with no number on the lid, is how
                a thorough engagement comes to look like a thin one. */}
            {settled.length} settled
            {settled.some((h) => h.status === 'confirmed') && (
              <span className="ml-0.5 rounded-full bg-red-100 px-1.5 text-[10px] font-bold text-red-700">
                {settled.filter((h) => h.status === 'confirmed').length}
              </span>
            )}
          </button>
          {showSettled && (
            <div className="space-y-1 border-l border-slate-200 pl-1 motion-safe:animate-panel-arrive">
              {settled.map(row)}
            </div>
          )}
        </>
      )}
    </div>
  );
}
